import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { brl } from "../lib/format";
import {
  Warning, UserPlus, Link as LinkIcon, ArrowClockwise, Check, MagnifyingGlass, Users,
} from "@phosphor-icons/react";

export default function OrphanHandles() {
  const [orphans, setOrphans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState({}); // { handle: { action, full_name, target_handle } }
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, c] = await Promise.all([
        api.get("/customers/orphan-handles"),
        api.get("/customers"),
      ]);
      setOrphans(o.data);
      setCustomers(c.data);
      // Default action: create with prefilled name = handle body
      const init = {};
      for (const h of o.data) {
        init[h.handle] = { action: "create", full_name: "", target_handle: "" };
      }
      setRows(init);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar");
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRow = (h, patch) => setRows(r => ({ ...r, [h]: { ...r[h], ...patch } }));

  const visible = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return orphans;
    return orphans.filter(o => (o.handle || "").toLowerCase().includes(f));
  }, [orphans, filter]);

  const applyOne = async (o) => {
    const r = rows[o.handle] || {};
    const item = {
      handle: o.handle,
      action: r.action || "create",
      full_name: r.full_name || o.handle.replace(/^@/, ""),
      target_handle: r.target_handle || null,
    };
    if (item.action === "link" && !item.target_handle) {
      return toast.error(`Escolha a @ destino para ${o.handle}`);
    }
    if (item.action === "create" && !(item.full_name || "").trim()) {
      return toast.error(`Informe o nome para ${o.handle}`);
    }
    setSaving(true);
    try {
      const { data } = await api.post("/customers/orphan-handles/bulk", { items: [item] });
      const result = data.results?.[0];
      if (result?.status === "error") {
        toast.error(`${o.handle}: ${result.error}`);
      } else if (result?.status === "linked") {
        toast.success(`${o.handle} vinculada · ${result.orders_updated || 0} pedido(s) movidos`);
      } else {
        toast.success(`${o.handle} cadastrada`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro no processamento");
    } finally { setSaving(false); }
  };

  const applyAll = async () => {
    const items = visible.map(o => {
      const r = rows[o.handle] || {};
      return {
        handle: o.handle,
        action: r.action || "create",
        full_name: r.full_name || o.handle.replace(/^@/, ""),
        target_handle: r.target_handle || null,
      };
    });
    // Validate quick before sending
    for (const it of items) {
      if (it.action === "link" && !it.target_handle) {
        return toast.error(`Escolha a @ destino para ${it.handle}`);
      }
      if (it.action === "create" && !(it.full_name || "").trim()) {
        return toast.error(`Informe o nome para ${it.handle}`);
      }
    }
    setSaving(true);
    try {
      const { data } = await api.post("/customers/orphan-handles/bulk", { items });
      const okCount = data.results.filter(r => r.status !== "error").length;
      const errCount = data.results.length - okCount;
      if (errCount) toast.error(`${errCount} falharam · ${okCount} OK`);
      else toast.success(`${okCount} @s processadas com sucesso`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro no processamento");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="orphan-handles-page" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="label-brutal">Auditoria</p>
          <h1 className="font-heading text-3xl sm:text-5xl font-black">@s órfãs</h1>
          <p className="text-brand-ink2 mt-2">
            Estas @arrobas aparecem em pedidos mas <strong>não têm cadastro de cliente</strong>.
            Cadastre em lote ou linke a um cliente existente.
          </p>
        </div>
        <button onClick={load} data-testid="orphan-refresh" className="btn-white h-11">
          <ArrowClockwise weight="bold" size={16} /> Atualizar
        </button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard icon={Warning} bg="bg-brand-peach" label="@s órfãs" value={orphans.length} />
        <StatCard icon={Users} bg="bg-brand-mint" label="Clientes cadastrados" value={customers.length} />
        <div className="card-brutal p-3 bg-white flex items-center gap-2">
          <MagnifyingGlass weight="bold" size={18} />
          <input
            data-testid="orphan-filter"
            className="input-brutal !h-10"
            placeholder="Filtrar @…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="card-brutal p-10 text-center font-bold">Carregando…</div>
      ) : visible.length === 0 ? (
        <div className="card-brutal p-10 text-center">
          <Check weight="bold" size={40} className="mx-auto text-brand-success" />
          <p className="font-heading text-xl font-black mt-3">Tudo em ordem!</p>
          <p className="text-brand-ink2 text-sm">Nenhuma @ órfã encontrada.</p>
        </div>
      ) : (
        <>
          <div className="card-brutal overflow-x-auto">
            <table className="tbl w-full min-w-[820px]">
              <thead>
                <tr>
                  <th>@arroba</th>
                  <th className="w-20 text-right">Pedidos</th>
                  <th className="w-24 text-right">Itens</th>
                  <th className="w-32 text-right">Receita</th>
                  <th className="w-40">Última compra</th>
                  <th className="w-[440px]">Ação</th>
                  <th className="w-28 text-right">Aplicar</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(o => {
                  const r = rows[o.handle] || { action: "create" };
                  return (
                    <tr key={o.handle} data-testid={`orphan-row-${o.handle}`}>
                      <td>
                        <p className="font-bold">{o.handle}</p>
                        {o.samples?.length ? (
                          <p className="text-[11px] text-brand-ink2 truncate max-w-[240px]" title={o.samples.join(" · ")}>
                            {o.samples.join(" · ")}
                          </p>
                        ) : null}
                      </td>
                      <td className="text-right">{o.orders_count}</td>
                      <td className="text-right">{o.items_count}</td>
                      <td className="text-right font-bold">{brl(o.revenue)}</td>
                      <td className="text-sm">{o.last_at ? new Date(o.last_at).toLocaleDateString("pt-BR") : "—"}</td>
                      <td>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="inline-flex border-2 border-brand-ink">
                            <button
                              type="button"
                              data-testid={`orphan-action-create-${o.handle}`}
                              onClick={() => setRow(o.handle, { action: "create" })}
                              className={`px-2 py-1 text-xs font-bold ${r.action === "create" ? "bg-brand-pink text-white" : "bg-white"}`}
                            >
                              <UserPlus weight="bold" size={12} className="inline mr-1" /> Cadastrar
                            </button>
                            <button
                              type="button"
                              data-testid={`orphan-action-link-${o.handle}`}
                              onClick={() => setRow(o.handle, { action: "link" })}
                              className={`px-2 py-1 text-xs font-bold border-l-2 border-brand-ink ${r.action === "link" ? "bg-brand-pink text-white" : "bg-white"}`}
                            >
                              <LinkIcon weight="bold" size={12} className="inline mr-1" /> Linkar
                            </button>
                          </div>
                          {r.action === "create" ? (
                            <input
                              data-testid={`orphan-name-${o.handle}`}
                              className="input-brutal !h-9 !py-1 text-sm flex-1 min-w-[160px]"
                              placeholder="Nome completo"
                              value={r.full_name || ""}
                              onChange={(e) => setRow(o.handle, { full_name: e.target.value })}
                            />
                          ) : (
                            <select
                              data-testid={`orphan-target-${o.handle}`}
                              className="input-brutal !h-9 !py-1 text-sm flex-1 min-w-[180px]"
                              value={r.target_handle || ""}
                              onChange={(e) => setRow(o.handle, { target_handle: e.target.value })}
                            >
                              <option value="">— Selecione o cliente destino —</option>
                              {customers.map(c => (
                                <option key={c.id} value={c.handle}>
                                  {c.handle} · {c.full_name}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </td>
                      <td className="text-right">
                        <button
                          type="button"
                          data-testid={`orphan-apply-${o.handle}`}
                          onClick={() => applyOne(o)}
                          disabled={saving}
                          className="btn-pink !h-9 text-xs"
                          title="Processar somente esta @"
                        >
                          {(rows[o.handle] || {}).action === "link" ? "Linkar" : "Cadastrar"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end">
            <button
              data-testid="orphan-apply-all"
              onClick={applyAll}
              disabled={saving}
              className="btn-white"
            >
              {saving ? "Processando…" : `Aplicar em lote (${visible.length})`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, bg, label, value }) {
  return (
    <div className={`card-brutal p-3 ${bg} flex items-center gap-3`}>
      <Icon weight="bold" size={22} />
      <div>
        <p className="text-[10px] font-bold uppercase">{label}</p>
        <p className="font-heading text-2xl font-black">{value}</p>
      </div>
    </div>
  );
}
